<template>
  <div class="gs-app dashboard gm-dashboard-refresh">
    <!-- Can mail/Upgraded user/Sandbox mode check  -->
    <div
      class="w-full btn-radius bg-white text-grey-700 p-4 mb-4"
      v-if="showSenderWithSmtpBanner && this.userCanMail"
    >
      <div class="flex items-center text-sm items-center justify-between">
        <div class="flex items-center">
          <InfoIcon class="mr-2 text-grey-600 h-4 w-4"/>
          {{ $t('frontend_groovemail_email_sender_warning_banner') }}
        </div>
        <div class="flex items-center">
          <div
            class="text-sm text-primary cursor-pointer font-bold mr-6"
            @click="moveToSenderCreation"
          >
            {{ $t('frontend_create') }}/{{ $t('frontend_edit') }}
            {{ $t('frontend_groovemail_settings_sender_settings') }}
          </div>
          <span class="cursor-pointer" @click="closeSenderWithSmtpBanner()">
            <CloseIcon class="text-grey-700 h-6 w-6"/>
          </span>
        </div>
      </div>
    </div>
    <!-- Can mail/Upgraded user/Sandbox mode check  -->

    <div class="mb-10 groovemail-quick-links">
      <quick-links :links="quickLinks" />
    </div>

    <div class="flex mb-10 justify-end">
      <div class="w-full flex flex-col sm:flex-row items-baseline sm:items-center sm:justify-between">
        <div class="mb-3 md:mb-0 md:w-auto">
          <p class="text-2xl font-bold text-bold text-deep-dark">
            {{ $t('frontend_groovemail_dashboard_overview') }}
          </p>
        </div>
        <div class="w-auto mail-dash-date-picker gs-refresh-controls">
          <date-range-picker
            ref="picker"
            v-model="dashboardDateRange"
            :opens="opens"
            :autoApply="autoApply"
            :max-date="maxDateForFilter"
            :startDate="customStartDate"
            :endDate="customEndDate"
            :ranges="customDateRanges"
            @update="fetchChartData"
          >
            <div slot="input" class="flex" slot-scope="picker">
              <div
                class="flex items-center bg-white px-4 py-2 border border-grey-200 rounded"
              >
                <p class="mr-4 text-md text-deep-dark">
                  {{ picker.startDate | moment('DD MMMM, YYYY') }}
                </p>
                <CalenderIcon class="text-grey-500 h-5 w-5 min-w-4"/>
              </div>
              <div class="mx-2 pt-2"><span>-</span></div>
              <div
                class="flex items-center bg-white px-4 py-2 border border-grey-200 rounded"
              >
                <p class="mr-4 text-md text-deep-dark">
                  {{ picker.endDate | moment('DD MMMM, YYYY') }}
                </p>
                <CalenderIcon class="text-grey-500 h-5 w-5 min-w-4"/>
              </div>
            </div>
          </date-range-picker>
        </div>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row">
      <div class="w-full lg:w-5/12">
        <DahboardStatsTabs :dashStatsData="dashStatsData" />
      </div>
      <div class="lg:w-9/12 bg-white pt-5 text-deep-dark btn-radius gm-dashboard-refresh__analytics">
        <div class="w-full">
            <div class="flex w-full border-b border-grey-200">
              <gd-tabs
                :tabs="analyticsTabs"
                :current-tab="analyticsTab"
                :wrapper-class="'px-1 gs-default-tabs text-grey-500 text-sm'"
                :tab-class="'mx-4 pb-3 gs-default-tabs-item'"
                :tab-active-class="
                  'border-primary border-b-2 text-primary gs-default-tabs-item-active'
                "
                :line-class="'gs-default-tabs-active-line'"
                @onClick="handleTabChange"
              />
            </div>
            <div class="mt-6 flex flex-col">
              <div>
                <div class="w-full flex flex-col">
                  <DahboardChartStat :chartStat="getChartStatistics" />
                </div>
              </div>
            </div>
          </div>
          <div class="flex w-full mb-6" v-if="hasSeriesDataLoaded">
            <div class="w-full max-h-96">
              <vue-apex-charts
                class="px-2 max-h-96"
                ref="realtimeChart"
                type="bar"
                height="300"
                :options="chartOptions"
                :series="series"
              ></vue-apex-charts>
            </div>
          </div>
          <div class="w-full mb-6" v-else>
            <chartContentLoader />
          </div>
      </div>
    </div>

    <gs-named-modal
      id="show-mail-walkthrough-video"
      name="show-mail-walkthrough-video"
      :title="$t('frontend_groovemail_dashboard_getting_started_with_groovemail_header')"
    >
      <div class="gs-app p-6 gm-dashboard-refresh__dialog">
        <ContentInIframe
          iframeSrc="https://www.youtube.com/embed/QHPR0MfcVjU"
          iframeClasses="border border-grey-200"
          iframeWidth="800"
          iframeHeight="451"
          :iframeTitle="$t('frontend_groovemail_dashboard_project_walkthrough_video_title')"
          iframeAllowData="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        >
        </ContentInIframe>
      </div>
    </gs-named-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import QuickLinks from '@groovepages/gd-ui-kit/components/QuickLinks';
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import VueApexCharts from 'vue-apexcharts';
import DahboardStatsTabs from './dash-stat-tabs.vue';
import DahboardChartStat from './dash-chart-stat.vue';
import dateFiltersMixin from '@/mixin/dateFiltersMixin';
import ContentInIframe from '@/components/content-in-iframe.vue';
import { dashboardUrls, sendersUrl } from '@/utils/urls.js';
import { getData } from '@/utils/apiHelper.js';
import themeConfig from '@/theme/themeConfig';

import tableContentLoader from '@/components/content-loader/tablecontentloader';
import chartContentLoader from '@/components/content-loader/chartcontentloader';

// import icons
import CampaignsIcon from '@/assets/images/icons/new/email_new.svg';
import ListIcon from '@/assets/images/icons/new/list_menu_new.svg';
import AutomateIcon from '@/assets/images/icons/new/automations_new.svg';
import FormsIcon from '@/assets/images/icons/new/forms_new.svg';
import SequenceIcon from '@/assets/images/icons/new/sequence_new.svg';
import TemplateIcon from '@/assets/images/icons/new/template_new.svg';
import NewBroadcastIcon from '@/assets/images/icons/new/plus_solid_new.svg';
import CalenderIcon from '@/assets/images/icons/new/calendar_days_new.svg';
import InfoIcon from '@/assets/images/icons/new/info_new.svg';
import CloseIcon from '@/assets/images/icons/new/close_small_new.svg';

export default {
  middleware: 'auth',
  mixins: [dateFiltersMixin],
  data() {
    return {
      showSenderWithSmtpBanner: false,
      dashboardDateRange: {
        startDate: moment().startOf('month'),
        endDate: moment().endOf('month')
      },
      dateFormat: 'MM/DD/YYYY',
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_analytics_broadcast_norecord',
        emptyStateBtnText:
          'frontend_groovemail_dashboard_campaigns_top_campaigns_create_campaign_btn_text'
      },
      customStartDate: moment(),
      customEndDate: moment(),
      maxDateForFilter: new Date(),
      opens: 'left',
      autoApply: true,
      hasTopCampaignsLoaded: 0,
      hasSeriesDataLoaded: 0,
      hasOpensCountLoaded: 0,
      hasClicksCountLoaded: 0,
      hasFormSubmissionsCountLoaded: 0,
      totalsubmissions: 0,
      totalopens: 0,
      totalclicks: 0,
      topCampaigns: [],
      topCampaignsTableColumns: [
        'name',
        'unique_percent_click_rate',
        'unique_percent_open_rate'
      ],
      topCampaignsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc'
        },
        sortable: [],
        headings: {
          name: () => this.$t('frontend_name'),
          unique_percent_click_rate: () =>
            this.$t(
              'frontend_groovemail_dashboard_campaigns_top_campaigns_ctr_header'
            ),
          unique_percent_open_rate: () =>
            this.$t(
              'frontend_groovemail_dashboard_campaigns_top_campaigns_open_rate_header'
            )
        },
        filterByColumn: false,
        filterable: [],
        perPage: 6,
        alwaysShowPerPageSelect: false,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        }
      },
      /* for chart */
      series: [
        {
          name: '',
          data: []
        }
      ],
      chartOptions: {
        chart: {
          height: 350,
          type: 'bar',
          zoom: {
            enabled: false
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 4
          }
        },
        colors: [themeConfig.appPrimaryColor],
        dataLabels: {
          enabled: false
        },
        title: {
          text: '',
          align: 'left'
        },
        grid: {
          row: {
            colors: ['transparent'], // takes an array which will be repeated on columns
            opacity: 0.5
          },
          strokeDashArray: 5
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: '#a4a8c0',
              fontSize: '14px'
            }
          },
          axisBorder: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '14px',
              colors: ['#a4a8c0']
            }
          }
        }
      },
      analyticsTab: 'opens'
    };
  },

  metaInfo() {
    return { title: this.$t('frontend_groovemail_welcome_dashboard') };
  },

  components: {
    CloseIcon,
    CalenderIcon,
    QuickLinks,
    InfoIcon,
    ClientTable,
    VueApexCharts,
    tableContentLoader,
    chartContentLoader,
    DahboardStatsTabs,
    DahboardChartStat,
    ContentInIframe,
  },

  computed: {
    /**
     * Used to determine if user can send out emails.
     * @return {number} 1 - user can send out emails
     *                  0 - user is restricted from sending emails
     */
    //  Can mail/Upgraded user/Sandbox mode check
    userCanMail() {
      return this.getCanMail();
    },

    /**
     * Used for providing tabs to chart.
     * @return {array} - different tabs for viweing chart on different criteria (email open count, email links clicked count, form submission count)
     */
    analyticsTabs() {
      return [
        {
          title: this.$t(
            'frontend_groovemail_members_affiliate_dashboard_opens'
          ),
          value: 'opens'
        },
        {
          title: this.$t(
            'frontend_groovemail_members_affiliate_dashboard_clicks'
          ),
          value: 'clicks'
        },
        {
          title: this.$t('frontend_groovemail_members_affiliate_form'),
          value: 'forms_submission'
        }
      ];
    },

    /**
     * Used for providing quicklinks data.
     * @return {array} - different links type with callbacks
     */
    quickLinks() {
      return [
        {
          name: this.$t('frontend_groovemail_components_pricepoint_sidebar_broadcasts'),
          callback: () => this.$router.push({ name: 'Broadcasts' }),
          icon: CampaignsIcon
        },
        {
          name: this.$t('frontend_groovemail_components_pricepoint_sidebar_sequences'),
          callback: () => this.$router.push({ name: 'Sequences' }),
          icon: SequenceIcon
        },
        {
          name: this.$t('frontend_groovemail_components_pricepoint_sidebar_lists'),
          callback: () => this.$router.push({ name: 'Lists' }),
          icon: ListIcon
        },
        {
          name: this.$t('frontend_groovemail_components_pricepoint_sidebar_automations'),
          callback: () => this.$router.push({ name: 'Automations' }),
          icon: AutomateIcon
        },
        {
          name: this.$t('frontend_groovemail_components_pricepoint_sidebar_forms'),
          callback: () => this.$router.push({ name: 'Forms' }),
          icon: FormsIcon
        },
        {
          name: this.$t('frontend_groovemail_admin_global_templates_page_header'),
          callback: () =>
            this.$router.push({
              name: 'MyTemplates',
              query: { type: 'professionally-designed-templates' }
            }),
          icon: TemplateIcon
        },
        {
          name: this.$t('frontend_groovemail_dashboard_quicklinks_new_broadcast'),
          callback: () => this.$router.push({ name: 'NewCampaign' }),
          icon: NewBroadcastIcon
        }
      ];
    },

    /**
     * Returns dashboard statistics which include email opens, email link clicks & form submissions.
     * @return {array} - returns an array of dashboard statistics based on filters applied.
     */
    dashStatsData() {
      return [
        {
          id: 1,
          value: this.totalopens,
          title: this.$t(
            'frontend_groovemail_members_affiliate_dashboard_opens'
          ),
          hasLoaded: this.hasOpensCountLoaded
        },
        {
          id: 2,
          value: this.totalclicks,
          title: this.$t(
            'frontend_groovemail_members_affiliate_dashboard_clicks'
          ),
          hasLoaded: this.hasClicksCountLoaded
        },
        {
          id: 3,
          value: this.totalsubmissions,
          title: this.$t('frontend_groovemail_members_affiliate_form'),
          hasLoaded: this.hasFormSubmissionsCountLoaded
        },
      ];
    },

    /**
     * Returns dashboard chart statistics which can be email opens, email link clicks or form submissions.
     * @return {object} - returns chart statistics based on tab selected.
     */
    getChartStatistics() {
      let chartStats;
      if (this.analyticsTab === 'opens') {
        [chartStats] = this.dashStatsData;
      } else if (this.analyticsTab === 'clicks') {
        [, chartStats] = this.dashStatsData;
      } else if (this.analyticsTab === 'forms_submission') {
        [, , chartStats] = this.dashStatsData;
      }
      return chartStats;
    },

    /**	
     * Returns slot data for top campaigns table.
     * @return {array} details for slot columns for top campaigns table.
    */
    slotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          value: props => {
            return props.row.name;
          },
          classes: 'break-all'
        },
        {
          slotName: 'unique_percent_click_rate',
          slotType: 'computed',
          value: props => {
            return props.row.unique_percent_click_rate
              ? `${props.row.unique_percent_click_rate} %`
              : '0%';
          }
        },
        {
          slotName: 'unique_percent_open_rate',
          slotType: 'computed',
          value: props => {
            return props.row.unique_percent_open_rate
              ? `${props.row.unique_percent_open_rate} %`
              : '0%';
          }
        },
        {
          slotName: 'unsubscribe_leads',
          slotType: 'clickable',
          callback: props => this.showUnsubscribedLeads(props.row.id)
        }
      ];
    }
  },
  
  // Makes API calls simultaneously to fetch senders data, dashboard data and top campaigns data.
  async created() {
    Promise.all([
      this.fetchSendersData(true),
      this.fetchCounts(),
      this.fetchChartValue(true),
      this.fetchTopCampignList(true)
    ]);
  },

  methods: {
    ...mapGetters('mailing', ['getCanMail']),

    // Used to hide SMTP banner
    closeSenderWithSmtpBanner() {
      this.showSenderWithSmtpBanner = false;
    },

    // Used to fetch sender's data and show banner based on that
    async fetchSendersData(primaryLoading) {
      const senderData = await getData(sendersUrl.senders, null, primaryLoading);
      this.senders = senderData?.data;
      if (this.senders && this.senders.length) {
        this.showSenderWithSmtpBanner = false;
      } else {
        this.showSenderWithSmtpBanner = true;
      }
    },

    // Used to display opens count, click count and form submission count in chart
    async fetchChartValue(primaryLoading) {
      this.hasSeriesDataLoaded = 0;
      const { startDate } = this.dashboardDateRange;
      const { endDate } = this.dashboardDateRange;
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const openCountParams = {
        type: this.analyticsTab,
        from: moment(startDate).format(this.dateFormat),
        to: moment(endDate).format(this.dateFormat),
        diffDays
      };
      const response = await getData(dashboardUrls.dashboardChartValue, { params: openCountParams }, primaryLoading);
      if (this.series?.[0]?.data) {
        this.series[0].data = response?.data?.data;
        this.updateSeriesLine();
        this.hasSeriesDataLoaded = 1;
      }
    },

    // Used to fetch counts of open count, click count and form submission count
    fetchCounts() {
      Promise.all([
        this.fetchOpenCount(true),
        this.fetchClickCount(true),
        this.fetchFormSubmissionCount(true)
      ]);
    },

    // Used to fetch the open count data
    async fetchOpenCount(primaryLoading) {
      this.hasOpensCountLoaded = 0;
      const { startDate } = this.dashboardDateRange;
      const { endDate } = this.dashboardDateRange;
      const openCountParams = {
        type: 'opens',
        from: moment(startDate).format(this.dateFormat),
        to: moment(endDate).format(this.dateFormat)
      };
      const response = await getData(dashboardUrls.dashboardCountData, { params: openCountParams }, primaryLoading);
      if (response){
        this.totalopens = response?.data?.count;
        this.hasOpensCountLoaded = 1;
      }
    },

    // Used to fetch the click count data
    async fetchClickCount(primaryLoading) {
      this.hasClicksCountLoaded = 0;
      const { startDate } = this.dashboardDateRange;
      const { endDate } = this.dashboardDateRange;
      const clickCountParams = {
        type: 'clicks',
        from: moment(startDate).format(this.dateFormat),
        to: moment(endDate).format(this.dateFormat)
      };
      const response = await getData(dashboardUrls.dashboardCountData, { params: clickCountParams }, primaryLoading);
      if (response){
        this.totalclicks = response?.data?.count;
        this.hasClicksCountLoaded = 1;
      }
    },

    // Used to fetch the form submission count data
    async fetchFormSubmissionCount(primaryLoading) {
      this.hasFormSubmissionsCountLoaded = 0;
      const { startDate } = this.dashboardDateRange;
      const { endDate } = this.dashboardDateRange;
      const submissionCountParams = {
        type: 'submissions',
        from: moment(startDate).format(this.dateFormat),
        to: moment(endDate).format(this.dateFormat)
      };
      const response = await getData(dashboardUrls.dashboardCountData, { params: submissionCountParams }, primaryLoading);
      if (response) {
        this.totalsubmissions = response?.data?.count;
        this.hasFormSubmissionsCountLoaded = 1;
      }
    },

    // Used to fetch top campaigns listing based on open rate
    async fetchTopCampignList(primaryLoading) {
      const campaigns = await getData(dashboardUrls.dashboardTopCampaigns, null, primaryLoading);
      if (campaigns?.data) {
        this.topCampaigns = campaigns?.data;
      }
      this.hasTopCampaignsLoaded = 1;
    },

    // Used to redirect the user to sender's page
    moveToSenderCreation() {
      this.$router.push({ name: 'Settings', query: { type: 'sender' } });
    },

    // Used to redirect the user to broadcast's page
    moveToCampaignCreation() {
      this.$router.push({ name: 'Broadcasts' });
    },

    // Used to redirect the user to broadcast's page with opens filter
    moveToTopCampaigns() {
      this.$router.push({
        name: 'Broadcasts',
        query: { sortBy: 'opens', type: 'percent' }
      });
    },

    // Used to handle the change of tab and fetch chart data based on tab change
    async handleTabChange(newTab) {
      this.analyticsTab = newTab;
      this.fetchChartData();
    },

    // Used to fetch charts data after date range filter change
    async fetchChartData() {
      await Promise.all([this.fetchCounts(), this.fetchChartValue(true)]);
    },
    
    // Used to update chart
    updateSeriesLine() {
      this.$nextTick(() => {
        if (this.$refs && this.$refs.realtimeChart) {
          this.$refs.realtimeChart.updateSeries(
            [
              {
                data: this.series[0].data
              }
            ],
            false,
            true
          );
        }
      });
    }
  }
};
</script>

<style scoped>
.dashboard
  >>> #show-mail-walkthrough-video
  .bg-white.rounded-lg.shadow-2xl.relative.flex.flex-col.overflow-hidden {
  width: 850px !important;
}

.dashboard >>> .mail-dash-date-picker .vue-daterange-picker {
  padding: 0px !important;
}

.dashboard >>> .mail-dash-date-picker .vue-daterange-picker .daterangepicker {
  top: 45px !important;
  left: auto !important;
  right: 5px !important;
}

.dashboard
  >>> .mail-dash-date-picker
  .vue-daterange-picker
  .daterangepicker::before {
  left: auto !important;
  right: 5px !important;
}

.dashboard
  >>> .mail-dash-date-picker
  .vue-daterange-picker
  .daterangepicker::after {
  left: auto !important;
  right: 5px !important;
}

.dashboard >>> .mail-dash-date-picker .reportrange-text {
  color: #a4a8c0;
  position: relative;
  padding: 0rem !important;
  background: transparent !important;
  border: 0px !important;
  width: 100%;
}

.dashboard >>> .mail-dash-date-picker .date-range-sections {
  border: 1px;
}

.dashboard >>> .chart_section {
  background-color: #fff;
}

.dashboard >>> .dash-set-up-btn {
  border-radius: 6px !important;
  border: 1px solid #cccedc !important;
}

.dashboard >>> .dash-section-close-icon {
  right: 20px;
}

.dashboard >>> .gd-table .VueTables--client .row {
  display: none;
}

.dashboard >>> .gd-table .VueTables--client .VueTables__table {
  box-shadow: none !important;
  border-radius: 0px !important;
}

.dashboard >>> .gd-table .VueTables--client .VueTables__table th {
  padding: 24px 12px !important;
}

.gd-table .VueTables--client .VueTables__table td {
  padding-top: 24px !important;
  padding-bottom: 24px !important;
}

.dashboard
  >>> .groovemail-table
  .VueTables--client
  .VueTables__table
  tr:nth-child(odd) {
  background: #f8f8fc;
}

.dashboard >>> .gd-table .VueTables--client table th:nth-child(1) {
  padding-left: 40px !important;
}

.dashboard
  >>> .gd-table
  .VueTables--client
  .VueTables__table
  tr:nth-child(even),
.dashboard
  >>> .groovemail-table
  .VueTables--client
  .VueTables__table
  thead
  tr:first-child {
  background: #fff !important;
}

.dashboard
  >>> .groovemail-table
  .VueTables--client
  .VueTables__table
  th:last-child
  span {
  display: inline-block !important;
}

.dashboard >>> .no-camp-wrapper .new-dash-camp-add-btn {
  font-weight: 600;
  border-radius: 6px;
  font-size: 16px;
}

.dashboard >>> .groovemail-quick-links .fa-user-robot path {
  fill: #989ab3;
}

.dashboard >>> .groovemail-quick-links .fa-stream,
.dashboard >>> .groovemail-quick-links .fa-align-justify,
.dashboard >>> .groovemail-quick-links .fa-plus,
.dashboard >>> .groovemail-quick-links .fa-filter {
  width: 26px;
}
.dashboard >>> .vue-daterange-picker {
  width: 100%;
  padding-left: 5px;
  padding-right: 5px;
}

.gm-dashboard-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-dashboard-refresh__analytics {
  min-width: 0;
  padding-right: 24px;
  padding-bottom: 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-dashboard-refresh >>> .mail-dash-date-picker .bg-white {
  background: var(--gs-input);
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

@media (max-width: 640px) {
  .gm-dashboard-refresh {
    padding: 18px 12px 32px;
  }

  .gm-dashboard-refresh__analytics {
    padding-right: 16px;
    padding-bottom: 16px;
  }
}
</style>

<style>
/* custom style for chart */
.apexcharts-tooltip-marker {
  background: var(--brand-primary-dark) !important;
}

.apexcharts-bar-area {
  fill: var(--brand-primary-dark);
}
.apexcharts-line {
  stroke: var(--brand-primary-dark);
}
.apexcharts-gridRow {
  fill: var(--brand-primary-invert);
}

/* style for chart when clicked on legend */
.apexcharts-area-series
  .apexcharts-series-markers
  .apexcharts-marker.no-pointer-events,
.apexcharts-line-series
  .apexcharts-series-markers
  .apexcharts-marker.no-pointer-events,
.apexcharts-radar-series path,
.apexcharts-radar-series polygon {
  pointer-events: none;
  fill: var(--brand-primary-dark);
}
</style>
